import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "../Dashboard";
import { toast } from "sonner";
import { btn, card, errMsg, field, STAGES } from "./ui";

export default function StaffingPipeline() {
    const [jobs, setJobs] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [jobId, setJobId] = useState("");
    const [candidateId, setCandidateId] = useState("");
    const [rows, setRows] = useState([]);
    const [openId, setOpenId] = useState(null);

    const loadJobs = () => api.get("/ats-core/jobs").then((r) => setJobs(r.data));
    const loadCandidates = () => api.get("/ats-core/candidates", { params: { limit: 200 } }).then((r) => setCandidates(r.data));
    const loadPlacements = (id) => {
        const params = id ? { job_id: id } : {};
        return api.get("/ats-core/pipeline", { params }).then((r) => setRows(r.data));
    };

    useEffect(() => {
        Promise.all([loadJobs(), loadCandidates(), loadPlacements()]).catch((e) => toast.error(errMsg(e)));
    }, []);

    const place = async (e) => {
        e.preventDefault();
        try {
            await api.post("/ats-core/pipeline", { candidate_id: candidateId, job_id: jobId });
            toast.success("Op de pipeline gezet");
            await loadPlacements(jobId);
        } catch (err) {
            toast.error(errMsg(err));
        }
    };

    const move = async (id, stage) => {
        try {
            await api.post(`/ats-core/pipeline/${id}/stage`, { stage });
            await loadPlacements(jobId);
        } catch (err) {
            toast.error(errMsg(err));
        }
    };

    const selected = jobs.find((j) => j.id === jobId);
    const grouped = Object.fromEntries(STAGES.map(([id]) => [id, rows.filter((r) => r.stage === id)]));

    return (
        <PageHeader
            title="Pipeline"
            subtitle="Kandidaat ↔ job. Stages zijn de bestaande staffing-set. Een draft-job weigert plaatsing tot bevestiging."
        >
            <form onSubmit={place} className={`${card} flex flex-wrap gap-3 mb-6 items-end`} data-testid="pipeline-form">
                <label className="text-xs text-[var(--jr-text-2)] flex-1 min-w-[200px]">
                    Job
                    <select className={`${field} mt-1`} value={jobId} data-testid="pipeline-job"
                        onChange={(e) => { setJobId(e.target.value); loadPlacements(e.target.value); }}>
                        <option value="">Kies een job</option>
                        {jobs.map((j) => (
                            <option key={j.id} value={j.id}>{j.title} ({j.status})</option>
                        ))}
                    </select>
                </label>
                <label className="text-xs text-[var(--jr-text-2)] flex-1 min-w-[200px]">
                    Kandidaat
                    <select className={`${field} mt-1`} value={candidateId} data-testid="pipeline-candidate"
                        onChange={(e) => setCandidateId(e.target.value)}>
                        <option value="">Kies een kandidaat</option>
                        {candidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </label>
                <button className={btn} disabled={!jobId || !candidateId} data-testid="pipeline-place">Plaatsen</button>
            </form>
            {selected && selected.status !== "active" && (
                <p className="mb-4 text-sm text-[var(--jr-amber)]" data-testid="pipeline-draft-warning">
                    Deze job is {selected.status}. Plaatsen lukt pas na human confirm.
                </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2" data-testid="pipeline-board">
                {STAGES.map(([id, label]) => (
                    <div key={id} className="rounded-xl border border-white/8 bg-[var(--jr-surface)] min-h-[180px]">
                        <div className="px-2 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--jr-text-3)] border-b border-white/8">
                            {label} · {(grouped[id] || []).length}
                        </div>
                        <div className="p-2 space-y-2">
                            {(grouped[id] || []).map((p) => (
                                <div key={p.id} className="rounded-lg border border-white/10 p-2" data-testid={`placement-${p.id}`}>
                                    <button className="text-left text-sm text-white w-full" onClick={() => setOpenId(openId === p.id ? null : p.id)}>
                                        {p.candidate_name}
                                    </button>
                                    <select className={`${field} mt-2 text-xs`} value={p.stage} data-testid={`placement-stage-${p.id}`}
                                        onChange={(e) => move(p.id, e.target.value)}>
                                        {STAGES.map(([sid, sl]) => <option key={sid} value={sid}>{sl}</option>)}
                                    </select>
                                    {openId === p.id && (
                                        <ul className="mt-2 space-y-1 text-[10px] text-[var(--jr-text-2)]" data-testid={`history-${p.id}`}>
                                            {(p.history || []).map((h, i) => (
                                                <li key={i}>{h.from_stage || "—"} → {h.to_stage}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </PageHeader>
    );
}
